'use client'

import type React from 'react'
import { useRef, useState, useCallback, useMemo } from 'react'
import { Upload, FileText, X, AlertCircle } from 'lucide-react'

interface FileUploadProps {
  name: string
  accept?: string
  required?: boolean
  maxSizeMB?: number
}

const FileUpload: React.FC<FileUploadProps> = ({
  name,
  accept = '.pdf,.doc,.docx,.rtf',
  required,
  maxSizeMB = 10,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [fileSize, setFileSize] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleClick = useCallback(() => {
    inputRef.current?.click()
  }, [])

  const allowedExtensions = useMemo(
    () => accept.split(',').map((s) => s.trim().toLowerCase()),
    [accept],
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) {
        setFileName(null)
        setFileSize(null)
        setError(null)
        return
      }

      // Validate size
      const maxBytes = maxSizeMB * 1024 * 1024
      if (file.size > maxBytes) {
        // Reject - clear the input so it won't be submitted
        e.target.value = ''
        setFileName(null)
        setFileSize(null)
        setError(`File is too large. Maximum size is ${maxSizeMB} MB.`)
        return
      }

      // Validate extension
      const ext = '.' + file.name.split('.').pop()?.toLowerCase()
      if (!allowedExtensions.includes(ext)) {
        e.target.value = ''
        setFileName(null)
        setFileSize(null)
        setError(
          `Unsupported file type. Please upload ${allowedExtensions.join(', ')}`,
        )
        return
      }

      setError(null)
      setFileName(file.name)
      setFileSize(formatSize(file.size))
    },
    [maxSizeMB, allowedExtensions],
  )

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (inputRef.current) {
      inputRef.current.value = ''
    }
    setFileName(null)
    setFileSize(null)
    setError(null)
  }, [])

  return (
    <div>
      {/* The actual file input - hidden but functional */}
      <input
        ref={inputRef}
        id={name}
        name={name}
        type="file"
        accept={accept}
        required={required}
        onChange={handleChange}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />

      {fileName ? (
        /* ── Selected file display ── */
        <div className="flex items-center gap-3 rounded-lg border border-input bg-background p-3 sm:p-4">
          <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-medium text-foreground truncate">
              {fileName}
            </p>
            {fileSize && (
              <p className="text-[11px] sm:text-xs text-muted-foreground">
                {fileSize}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleClick}
              className="text-xs sm:text-sm text-primary hover:underline font-medium"
            >
              Change
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Remove file"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        /* ── Empty upload zone (with error variant) ── */
        <button
          type="button"
          onClick={handleClick}
          className={[
            'w-full flex flex-col items-center gap-2 text-center cursor-pointer rounded-lg border-2 border-dashed p-4 sm:p-5 transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            error
              ? 'border-red-300 bg-red-50/50 hover:border-red-400'
              : 'border-input bg-muted/30 hover:border-primary/50 hover:bg-muted/50',
          ].join(' ')}
        >
          <Upload
            className={`w-5 h-5 sm:w-6 sm:h-6 ${error ? 'text-red-400' : 'text-muted-foreground'}`}
          />
          <p className="text-xs sm:text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Click to upload</span>{' '}
            or drag and drop
          </p>
          <p className="text-[11px] sm:text-xs text-muted-foreground">
            PDF or DOC/DOCX, up to ~{maxSizeMB} MB
          </p>
        </button>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-1.5 mt-1.5 text-xs sm:text-sm text-red-600">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}

export default FileUpload
